import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';

import { currencyCode } from 'Helpers/Geo/data';
import CouponSaving from 'Components/Product/ProductCommerce/CouponSaving';
import Price from 'Components/Utility/Price/Price';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import SpecialFormattedNumber from 'Components/Utility/Price/SpecialFormattedNumber';

function ProductHeaderPrices({ product, voucher, showcase }) {
  /* eslint-disable max-len */
  const bundles = get(product, 'bundles', []);
  const currentPrice = get(product, 'currentPrice', {});
  const discountPercentOverride = get(product, 'discount_percent_override', 0);
  const firstPickAndMixTier = get(product, 'firstPickAndMixTier', {});
  const freeToPlay = get(product, 'free_to_play', false);
  const fullPrice = get(product, 'fullPrice', {});
  const hideDiscount = get(product, 'hideDiscount', false);
  const mystery = get(product, 'mystery', false);
  const oneQuantityPickAndMix = get(firstPickAndMixTier, 'quantity', 0) === 1 || false;
  const payWhatYouWant = get(product, 'pay_what_you_want', false);
  const presale = get(product, 'presale', false);
  const price = get(product, 'price', {});
  const srpBundle = get(product, 'is_srp_bundle', false);
  const ztormId = get(product, 'ztormId', '');

  let type = get(product, 'type', '');
  if (srpBundle) type = null;

  // DISCOUNT PERCENTAGE LOGIC
  const currentDiscount = get(product, 'current_discount', {});
  let currentDiscountPercent = get(currentDiscount, 'percent', 0);
  if (oneQuantityPickAndMix) currentDiscountPercent = 0; // Discounts don't apply to 1 item tier pick & mix

  // COUPON PRICE LOGIC
  const validCoupon = ProductRequirements.validCoupon(product, voucher);
  let couponDiscountPercent = (get(validCoupon, 'percent', 0) / 100);
  if (oneQuantityPickAndMix) couponDiscountPercent = 0; // Not valid on 1 item tier pick & mix
  const totalSaving = ProductRequirements.totalSaving(product, validCoupon);
  let productHasValidCoupon = !!validCoupon;
  if (totalSaving.isMonetary) productHasValidCoupon = false;

  // PICK & MIX LOGIC
  let oneQuantityPnmSavingPercent = 0;
  if (firstPickAndMixTier && firstPickAndMixTier.price) {
    oneQuantityPnmSavingPercent = Math.abs(
      Math.round(((firstPickAndMixTier.price[currencyCode] - price[currencyCode]) / price[currencyCode]) * 100),
    );
    if (oneQuantityPnmSavingPercent > 99) oneQuantityPnmSavingPercent = 99;
  }

  // SET UP OF DISPLAY PRICES
  let priceToDisplay = {};
  let wasPriceToDisplay = {};
  let discountToDisplay = 0;

  // Prices when a game is NOT on sale
  if (type !== 'bundle' && currentDiscountPercent === 0) {
    priceToDisplay = price;
  }

  // Prices when a game IS on sale
  if (type !== 'bundle' && currentDiscountPercent > 0) {
    priceToDisplay = price;
    wasPriceToDisplay = price;
    discountToDisplay = currentDiscountPercent;
  }

  // Prices when a bundle is using the product template
  if (type === 'bundle' && bundles.length === 1) {
    priceToDisplay = currentPrice || price;
    wasPriceToDisplay = fullPrice;
    discountToDisplay = discountPercentOverride > 0 ? discountPercentOverride / 100 : currentDiscountPercent;
  }

  // Prices when the product is in a 1 item tier pick & mix
  if (oneQuantityPickAndMix) {
    priceToDisplay = firstPickAndMixTier.price;
    wasPriceToDisplay = price;
    discountToDisplay = oneQuantityPnmSavingPercent / 100;
  }

  // Prices when the product has a valid coupon applied
  if (productHasValidCoupon && !oneQuantityPickAndMix) {
    priceToDisplay = price;
    wasPriceToDisplay = (type === 'bundle' && !mystery) ? fullPrice : price;
    discountToDisplay = Math.abs((totalSaving.percent / 100));
    if (discountToDisplay > 0.99) discountToDisplay = 0.99;
  }

  if (ztormId && (price[currencyCode] === 9999900 || price[currencyCode] === 99900)) return false;
  if (payWhatYouWant || presale || freeToPlay) return false;

  if (isEmpty(priceToDisplay)) return false;

  return (
    <React.Fragment>
      <div className="PriceContainer">
        {showcase &&
          <div className="PriceContainer__prices">
            <div className="PriceContainer__price">
              <Price
                showZero
                price={priceToDisplay}
                type={type}
                discount={currentDiscountPercent}
                bonusDiscount={couponDiscountPercent}
              />
            </div>
            {!hideDiscount &&
              <div className="PriceContainer__wasPrice">
                <Price
                  price={wasPriceToDisplay}
                />
              </div>
            }
          </div>
        }
        {!showcase &&
          <>
            <div className="PriceContainer__price">
              <b>
                <Price
                  showZero
                  price={priceToDisplay}
                  type={type}
                  discount={currentDiscountPercent}
                  bonusDiscount={couponDiscountPercent}
                />
              </b>
            </div>
            {!hideDiscount &&
              <div className="PriceContainer__wasPrice">
                <Price
                  price={wasPriceToDisplay}
                />
              </div>
            }
          </>
        }
        {!hideDiscount && discountToDisplay > 0 &&
          <div className="PriceContainer__savingPercentage">
            -<SpecialFormattedNumber value={discountToDisplay} style="percent" />
          </div>
        }
      </div>

      {!showcase && productHasValidCoupon && !oneQuantityPickAndMix &&
        <CouponSaving
          savingPrice={totalSaving.price}
          hasExtraSaving={totalSaving.hasExtraSaving}
          extraSavingPrice={totalSaving.extraSavingPrice}
          coupon={validCoupon}
          isMonetary={totalSaving.isMonetary}
        />
      }
    </React.Fragment>
  );
}

ProductHeaderPrices.propTypes = {
  product: PropTypes.shape({}),
  voucher: PropTypes.shape({}),
  showcase: PropTypes.bool,
};

ProductHeaderPrices.defaultProps = {
  product: {},
  voucher: {},
  showcase: false,
};

const mapStateToProps = ({
  voucher: { voucher },
}) => ({
  voucher,
});

export default connect(mapStateToProps)(ProductHeaderPrices);
